import { useDispatch, useSelector } from 'react-redux';
import { Button, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { selectUser } from '@app/redux/auth';
import { selectBillingInfo } from '@app/redux/billingInfo';
import { Issuer, selectConfig } from '@app/redux/config';
import { selectLocale } from '@app/redux/locale';
import { getUnpaidInvoice } from '@app/utils';

import { BillingStatus, Invoice, Locale } from '@mfe/shared/schema-types';

import { goToPayment } from '../../../navigation';
import { InvoiceStatusChip } from '../../invoice-status-chip';
import { BillingAutoPayMethod } from '../autopay-method';
import { BillingSummaryHeader } from '../billing-summary-header';

import styles from './styles.module.scss';

const Summary = (): JSX.Element => {
  const { ref: balanceRef } = useInView({
    threshold: 1,
  });
  const { ref: dateRef } = useInView({ threshold: 1 });
  const {
    billingInfo: { autoPay, billingAccount, invoices, summaryInvoiceStatus, totalBalance },
  } = useSelector(selectBillingInfo);
  const {
    locale: { userLocale: locale },
  } = useSelector(selectLocale);
  const {
    user: { productInstanceStatus },
  } = useSelector(selectUser);

  const { issuer } = useSelector(selectConfig);
  const isSalesforce = issuer === Issuer.Salesforce;

  const { t } = useTranslation('Summary');

  const invoice = getUnpaidInvoice(locale, invoices);

  const isPaymentButtonDisabled = (locale === Locale.PtBr && totalBalance.value === 0) || isSalesforce;

  return (
    <div className={styles['container']}>
      <div className={styles['headerContainer']} ref={dateRef} id="header">
        <BillingSummaryHeader
          productInstanceStatus={productInstanceStatus}
          billingAccount={billingAccount}
          locale={locale}
        />
      </div>

      {locale === Locale.PtBr && summaryInvoiceStatus && (
        <div className={styles['badgeContainer']}>
          <InvoiceStatusChip badgeStatus={summaryInvoiceStatus} />
        </div>
      )}

      {billingAccount?.billingStatus !== BillingStatus.New && (
        <div className={styles['balanceContainer']} ref={balanceRef}>
          <div className={styles['balance']}>
            <Txt variant="heading1" color="regular" mb="12px">
              {totalBalance.text}
            </Txt>
            <Txt variant="bodySmallRegular" color="regular">
              {t('amountDue')}
            </Txt>
          </div>
        </div>
      )}

      <div className={styles['autoPayContainer']}>
        <BillingAutoPayMethod autoPay={autoPay} />
      </div>

      <div className={styles['paymentButtonContainer']}>
        <PaymentButton invoice={invoice} disabled={isPaymentButtonDisabled} />
      </div>
    </div>
  );
};

type Props = {
  invoice?: Invoice | null;
  disabled?: boolean;
};

const PaymentButton = ({ invoice, disabled = false }: Props): JSX.Element => {
  const { t } = useTranslation('Summary');
  const dispatch = useDispatch();

  return (
    <Button
      data-cy="make-payment-button"
      variant="primary"
      fluid
      disabled={disabled}
      onClick={() => goToPayment(dispatch, invoice)}
    >
      <Txt variant="labelLarge" theme="dark" color="regular">
        {t('makeOneTimePayment')}
      </Txt>
    </Button>
  );
};

export default Summary;
